import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useImport } from "@/context/ImportContext";

export default function ImportaExcel() {
  const { uploadFile, loading, result, resetResult } = useImport();
  const [file, setFile] = useState<File | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
        resetResult();
      }
    },
    [resetResult]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) return;
    try {
      await uploadFile(file);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Carica un file Excel (.xlsx) per importare contatti e lead nel database.
      </p>

      <div
        {...getRootProps()}
        className={`flex min-h-[220px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition ${
          isDragActive
            ? "border-secondary/50 bg-secondary/5"
            : "border-slate-200 bg-slate-50/80 hover:border-secondary/30 hover:bg-slate-50"
        }`}
      >
        <input {...getInputProps()} />
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700">
          <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </span>
        {isDragActive ? (
          <p className="font-semibold text-secondary">Rilascia il file qui…</p>
        ) : (
          <>
            <p className="text-lg font-bold text-secondary">Trascina o clicca per caricare</p>
            <p className="mt-1 text-sm text-gray-400">Solo file .xlsx</p>
          </>
        )}
      </div>

      {file ? (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-secondary">{file.name}</span>
        </div>
      ) : null}

      <button
        type="button"
        onClick={handleUpload}
        disabled={!file || loading}
        className="mt-4 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? "Importazione in corso…" : "Avvia importazione"}
      </button>

      {result ? (
        <div
          className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
            result.success
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          <p className="font-semibold">{result.success ? "Import completato" : "Errore import"}</p>
          {result.message ? <p className="mt-1 opacity-90">{result.message}</p> : null}
          {result.righe_importate !== undefined ? (
            <p className="mt-1 text-xs opacity-75">Righe importate: {result.righe_importate}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
